import {
  DownloadInventoryRecord,
  GetInventoryLocationWithPagination,
  GetSingleInventoryDetailById,
} from '@/apis/inventory';
import {
  BellIcon,
  DeleteIcon,
  DownloadIcon,
  NavigateLinkIcon,
  PencilIcon,
} from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { CustomPagination, CustomShipmentText, TeamBadge } from '@/components';
import { useSpin } from '@/components/SpinContext';
import { useUnAuthorize } from '@/hooks';
import {
  Col,
  Divider,
  Empty,
  Flex,
  message,
  Row,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import InventoryCellTooltip from './InventoryCellTooltip';
import type { InventoryTooltipItem } from '../type';
import InventoryDeviceHistory from './InventoryDeviceHistory';
import InventoryInfoItem from './InventoryInfoItem';
import InventoryLocationMultiSelect from './InventoryLocationMultiSelect';
import InventorySerialsField from './InventorySerialsField';
import { saveBlobAsFile } from '@/pages/Shipments/components/fileDownload';

export interface InventoryLocations {
  name?: string;
  address?: string;
  location_id?: string;
  stock?: string;
  reorder_target?: string;
  total_required?: string;
  shipped_in?: string;
  shipped_out?: string;
  stock_state?: string;
  serial_numbers?: string;
  is_integration_managed?: boolean;
}
export interface InventoryDetails {
  id?: string;
  name?: string;
  sku?: string;
  description?: string;
  product?: {
    id?: string;
    name?: string;
  };
  notification_method?: string;
  has_notification?: boolean;
  can_save_notification?: boolean;
  teams?: {
    id?: string;
    name?: string;
    color?: string;
  }[];
  sub_items?: {
    id?: string;
    record_id?: string;
    product_id?: string;
    product?: {
      id?: string;
      name?: string;
    };
    name?: string;
    quantity?: number;
    left?: number;
    location?: InventoryTooltipItem[];
  }[];
  locations?: InventoryLocations[];
  select_all_locations?: boolean;
  history?: {
    title?: string;
    units?: string;
    time?: string;
  }[];
}

interface InventoryFormProps {
  id?: string;
  isEditMode?: boolean;
  // onClose?: () => void;
  handleDeleteClick?: (inventoryDetails: InventoryDetails) => void;
  handleEditInventory?: (
    isOpen?: boolean,
    name?: string,
    id?: string,
    isDuplicate?: boolean
  ) => void;
  showDetailDrawer?: boolean;
  onDetailsLoaded?: () => void;
  onOpenDeviceHistory?: () => void;
  hideActions?: boolean;
}

export const getStockStateStyle = (state?: string) => {
  switch (state) {
    case 'high':
      return {
        bgColor: 'var(--thin-green)',
        textColor: 'var(--green-900)',
        label: 'High',
      };

    case 'low':
      return {
        bgColor: 'var(--light-warning)',
        textColor: 'var(--warning)',
        label: 'Low',
      };

    default:
      return {
        bgColor: 'var(--light-gray)', // fallback bg
        textColor: 'var(--text-secondary)', // fallback text
        label: state ? state : 'Unknown',
      };
  }
};

export const handleDownloadSKU = async (
  id: string,
  token: string,
  unauthorize: (error?: unknown) => void
) => {
  if (!id) return;
  try {
    const response = await DownloadInventoryRecord(id, token);
    saveBlobAsFile(response, `${id || 'inventory-record'}.xlsx`);
  } catch (error) {
    unauthorize(error);
    message.error('Failed to download inventory record');
  }
};

const InventoryDetailDrawer = ({
  id,
  // onClose,
  handleDeleteClick,
  handleEditInventory,
  showDetailDrawer,
  onDetailsLoaded,
  onOpenDeviceHistory,
  hideActions = false,
}: InventoryFormProps) => {
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnAuthorize();
  const [inventoryDetails, setInventoryDetails] = useState<InventoryDetails>(
    {} as InventoryDetails
  );

  const [locationsList, setLocationsList] = useState<InventoryLocations[]>([]);
  const [page, setPage] = useState<number>(1);
  const [maxPageSize, setMaxPageSize] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(20);

  const handlePageChange = (newPage: number) => {
    const validPage = newPage > 0 ? newPage : 1;
    setPage(validPage);
  };
  const onDetailsLoadedRef = useRef(onDetailsLoaded);

  useEffect(() => {
    onDetailsLoadedRef.current = onDetailsLoaded;
  }, [onDetailsLoaded]);

  const fetchInventoryDetails = useCallback(
    async (id: string) => {
      try {
        showSpin();
        const res = await GetSingleInventoryDetailById(id, token);
        const data = res?.data?.data?.data ?? null;
        setInventoryDetails(data);
      } catch (error) {
        unauthorize(error);
      } finally {
        onDetailsLoadedRef.current?.();
        hideSpin();
      }
    },
    [showSpin, hideSpin, unauthorize, token]
  ); //

  useEffect(() => {
    if (showDetailDrawer) {
      fetchInventoryDetails(id);
      setPage(1);
    }
  }, [fetchInventoryDetails, id, showDetailDrawer]);

  const fetchInventoryLocations = useCallback(
    async (id?: string, page?: number) => {
      try {
        showSpin();
        const model = {
          page: page,
          page_size: pageSize,
        };
        const res = await GetInventoryLocationWithPagination(id, model, token);
        const data = res?.data?.data?.data ?? null;
        setLocationsList((data?.locations as InventoryLocations[]) || []);
        setTotalPages(data?.pagination?.total_pages || 1);
        setMaxPageSize(data?.pagination?.total_count || 0);
      } catch (error) {
        unauthorize(error);
      } finally {
        hideSpin();
      }
    },
    [showSpin, hideSpin, unauthorize, token, pageSize]
  ); //

  useEffect(() => {
    if (showDetailDrawer) {
      fetchInventoryLocations(id, page);
    }
  }, [fetchInventoryLocations, id, page, showDetailDrawer]);

  return (
    <div>
      <div className='drawer-header-1'>
        {!hideActions ? (
          <div className='drawer-header-2'>
            {inventoryDetails?.can_save_notification && (
              <span
                onClick={() =>
                  handleEditInventory(true, 'setup_notification', id, false)
                }
                className='h-4 cursor-pointer'
              >
                <BellIcon
                  color={'var(--text-primary)'}
                  width={16}
                  height={16}
                />
              </span>
            )}

            <span
              onClick={() =>
                handleEditInventory(true, 'inventory_form', id, false)
              }
              className='h-4 cursor-pointer'
            >
              <PencilIcon color='var(--text-primary)' />
            </span>
            <span
              onClick={() => handleDeleteClick(inventoryDetails)}
              className='h-4 cursor-pointer'
            >
              <DeleteIcon />
            </span>
            <span
              onClick={() => handleDownloadSKU(id, token, unauthorize)}
              className='h-4 cursor-pointer'
            >
              <DownloadIcon />
            </span>
          </div>
        ) : null}
      </div>

      <div className='custom-scrollbar inventory-detail-drawer-wrapper'>
        <Row gutter={[0, 16]}>
          <Typography.Text
            ellipsis={{ tooltip: inventoryDetails?.name }}
            className='zl-heading-20 text-uppercase'
          >
            {inventoryDetails?.name}
          </Typography.Text>
          <Col span={24}>
            <CustomShipmentText
              text={inventoryDetails?.description || '--'}
              color='var(--text-primary)'
              marginBottom={'0px'}
            />
          </Col>

          <Col span={24}>
            <CustomShipmentText
              text={'Team'}
              fontWeight='600'
              marginBottom={'0px'}
            />
            <Flex gap={8} wrap>
              {inventoryDetails?.teams?.length > 0 ? (
                <>
                  {inventoryDetails?.teams?.map((team, index) => (
                    <React.Fragment key={index}>
                      <TeamBadge
                        truncateLength={28}
                        color={team?.color}
                        text={team?.name}
                        tooltipText={team?.name}
                      />
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <Flex className='disabled-row font-12 my-2'>No Teams</Flex>
              )}
            </Flex>
          </Col>

          {hideActions ? (
            <Col span={24}>
              <CustomShipmentText
                text='Locations'
                fontWeight='600'
                marginBottom={'4px'}
              />
              <InventoryLocationMultiSelect
                inventoryDetails={inventoryDetails}
                onUpdated={() => {
                  if (id) {
                    fetchInventoryDetails(id);
                    fetchInventoryLocations(id, page);
                  } else {
                    return undefined;
                  }
                }}
              />
            </Col>
          ) : null}

          <Divider className='inventory-divider-style-8' />
          {/* sub items  */}
          <Col span={24}>
            <Flex gap={8} vertical>
              <CustomShipmentText
                text={'Sub-items'}
                fontSize={'14px'}
                fontWeight='600'
                marginBottom={'4px'}
              />
              {inventoryDetails?.sub_items?.length > 0 ? (
                <>
                  {inventoryDetails?.sub_items?.map((item, index) => (
                    <div className='inventory-row' key={index}>
                      <Flex
                        align='center'
                        justify='space-between'
                        className='w-full'
                      >
                        <Flex align='center' gap={8}>
                          <p className='zl-heading-12'>
                            {item?.quantity}{' '}
                            <span className='zl-heading-12 gray-A4'>×</span>{' '}
                            {item?.name}{' '}
                          </p>
                          <span
                            onClick={() =>
                              fetchInventoryDetails(item?.record_id)
                            }
                            className='nav-icon h-4'
                          >
                            <NavigateLinkIcon />
                          </span>
                        </Flex>

                        <Tooltip
                          overlayClassName='inventory-tooltip'
                          title={
                            <InventoryCellTooltip items={item?.location} />
                          }
                        >
                          <span>
                            <CustomShipmentText
                              text={`${item?.left} left`}
                              lineHeight='20px'
                              marginBottom={'0px'}
                              marginTop={'0px'}
                            />
                          </span>
                        </Tooltip>
                      </Flex>
                    </div>
                  ))}
                </>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className='inventory-sub-items-empty'
                />
              )}
            </Flex>
          </Col>

          <Divider className='inventory-divider-style-8' />
          <div className='per-device-location-section-wrapper'>
            {locationsList?.map((location, index) => (
              <React.Fragment key={index}>
                <Col span={24}>
                  <Flex align='center' gap={8}>
                    <CustomShipmentText
                      text={location?.name}
                      fontSize={'14px'}
                      fontWeight='600'
                      marginBottom={'0px'}
                    />
                    {location?.stock_state && (
                      <Tag
                        style={{
                          backgroundColor: getStockStateStyle(
                            location?.stock_state
                          )?.bgColor,
                          color: getStockStateStyle(location?.stock_state)
                            ?.textColor,
                          border: 0,
                        }}
                        className='inventory-custom-tag'
                      >
                        {getStockStateStyle(location?.stock_state)?.label}
                      </Tag>
                    )}
                  </Flex>
                </Col>
                <Col span={24} className='mt-3'>
                  <Row gutter={[16, 12]}>
                    <InventoryInfoItem label='Stock' value={location?.stock} />
                    <InventoryInfoItem
                      label='Reorder target'
                      value={location?.reorder_target}
                    />
                    <InventoryInfoItem
                      label='Total required'
                      value={location?.total_required}
                    />

                    <InventoryInfoItem
                      label='Shipped in'
                      value={location?.shipped_in}
                    />
                    <InventoryInfoItem
                      label='Shipped out'
                      value={location?.shipped_out}
                    />

                    {location?.serial_numbers && (
                      <InventorySerialsField value={location?.serial_numbers} />
                    )}
                  </Row>
                </Col>
                {index !== locationsList?.length - 1 && (
                  <Divider className='inventory-divider-16' />
                )}
              </React.Fragment>
            ))}
          </div>

          {maxPageSize > 20 && (
            <Row className='w-full' justify='center'>
              <Col>
                <CustomPagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </Col>
            </Row>
          )}

          <Divider className='inventory-divider-style-8' />

          {id ? (
            <Col span={24}>
              <InventoryDeviceHistory
                inventoryId={id}
                onOpenFullHistory={() => onOpenDeviceHistory?.()}
              />
            </Col>
          ) : (
            <Col span={24}>
              <Flex gap={16} vertical>
                <CustomShipmentText
                  text={'History'}
                  fontSize={'14px'}
                  fontWeight='600'
                  marginBottom={'0px'}
                />
                <Flex gap={8} vertical>
                  {inventoryDetails?.history?.length > 0 ? (
                    <>
                      {inventoryDetails?.history.map((item, index) => {
                        const isPositive = !item.units?.startsWith('-');
                        return (
                          <Flex
                            align='center'
                            justify='space-between'
                            gap={12}
                            key={index}
                          >
                            {/* Event title — grows, wraps gracefully */}
                            <CustomShipmentText
                              text={item.title}
                              fontWeight='500'
                              lineHeight='18px'
                              marginBottom={'0px'}
                              style={{ flex: 1, minWidth: 0 }}
                            />

                            {/* Delta badge + timestamp — never wrap or collide */}
                            <Flex
                              align='center'
                              gap={8}
                              style={{ flexShrink: 0 }}
                            >
                              <Tag
                                style={{
                                  margin: 0,
                                  border: 0,
                                  fontWeight: 600,
                                  fontSize: 12,
                                  lineHeight: '18px',
                                  backgroundColor: isPositive
                                    ? 'var(--thin-green)'
                                    : 'var(--danger-thin)',
                                  color: isPositive
                                    ? 'var(--green-900)'
                                    : 'var(--danger)',
                                }}
                              >
                                {item.units}
                              </Tag>
                              <span
                                style={{
                                  fontSize: 12,
                                  color: 'var(--text-secondary)',
                                  whiteSpace: 'nowrap',
                                  minWidth: 92,
                                  textAlign: 'right',
                                }}
                              >
                                {item.time}
                              </span>
                            </Flex>
                          </Flex>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </>
                  )}
                </Flex>
              </Flex>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default InventoryDetailDrawer;
